import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  MenuItem,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { useTimer } from "../Context/TimerContext";
import ExpandableText from "./ExpandableText";
import {
  timerService,
  formatClock,
  formatDuration,
  canForceStopTimer,
} from "../services/timerService";

const POLL_MS = 5000;
const LONG_RUNNING_SECONDS = 2 * 60 * 60;
// Budget left at which the row stops informing and starts warning.
const BUDGET_WARN_SECONDS = 5 * 60;

// ── design tokens — rich but softened palette ───────────────────────────────
const INK = "#1F2430";
const SUB = "#6B7280";
const MUTED = "#9AA2B1";
const LINE = "#ECEEF2";
const BRAND = "#3E5AA6";
const BRAND2 = "#5B78C4";
// softened priority tones (used for the small dot + tint chip)
const PRIO = { low: "#4E9B6E", medium: "#C79245", high: "#C75B5B", critical: "#B0466E" };
const GREEN = "#3F9E6A";
const GREEN_BG = "#EDF7F0";
const AMBER = "#B07A2E";
const AMBER_BG = "#FBF3E4";
const CARD = {
  bgcolor: "#fff",
  border: `1px solid ${LINE}`,
  borderRadius: 3,
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
};

const AVATAR_PALETTE = ["#4560A6", "#5A7BC0", "#3E8B9B", "#4E9B6E", "#B98A4E", "#9270B8", "#B36A90", "#4E9188"];
const avatarColor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fmtSince = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleTimeString("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

// A tile is a button only when there is somewhere to go.
//
// "Departments active" and "Total live time" have no list behind them, so they
// stay plain: no pointer, no hover, no focus ring. A tile that looks clickable
// and does nothing reads as broken, and the reader has to click it to find out.
const StatTile = ({ icon, label, value, color, onClick, active }) => (
  <Box
    {...(onClick
      ? {
          role: "button",
          tabIndex: 0,
          onClick,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          },
        }
      : {})}
    sx={{
      ...CARD,
      flex: "1 1 150px",
      minWidth: 140,
      p: 1.75,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      ...(onClick
        ? {
            cursor: "pointer",
            userSelect: "none",
            transition: "box-shadow .15s, border-color .15s, transform .15s",
            // The active tile says which list you are looking at. Without it,
            // filtering to "Working now" and then scrolling looks identical to
            // the board simply being empty.
            borderColor: active ? color : undefined,
            boxShadow: active ? `0 0 0 2px ${color}33` : undefined,
            "&:hover": { transform: "translateY(-1px)", boxShadow: `0 4px 14px ${color}22` },
            "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: 2 },
          }
        : {}),
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${color}, ${color}d0)`,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: `0 3px 9px ${color}26`,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 21, fontWeight: 800, color: INK, lineHeight: 1.05, letterSpacing: "-0.4px" }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: SUB, fontWeight: 500 }}>{label}</Typography>
    </Box>
  </Box>
);

const LiveActivityDashboard = ({ embedded = false }) => {
  const { userInfo, role, allUsers } = useContext(AutoTaskAuthContext) || {};
  const timer = useTimer();

  // A Head may end anyone's session from here. A HOD may not — see
  // canForceStopTimer: reading the board is one permission, editing somebody
  // else's timesheet is another.
  const canStopOthers = canForceStopTimer(role || userInfo?.role);
  // The row waiting on a yes/no, and the session id currently being stopped.
  const [stopTarget, setStopTarget] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);
  const [toast, setToast] = useState(null); // { severity, message }

  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [todayByUser, setTodayByUser] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [dept, setDept] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("duration");

  // Which tile is driving the view. null = no tile filter.
  const [focus, setFocus] = useState(null);

  // The tiles switch what the board IS, rather than scrolling to a panel.
  //
  // Showing idle people underneath the live list meant scrolling past everyone
  // who is busy to reach everyone who is not. Clicking "Idle / available" now
  // replaces the board: the active sessions are gone and only idle people are
  // on screen. Clicking the lit tile again returns to the default view.
  const focusLive = () => {
    const next = focus === "live" ? null : "live";
    setFocus(next);
    if (next) {
      // Clearing the other filters is the point: "Working now: 7" and a board
      // showing 2 because a department filter is still set is a lie.
      setDept("all");
      setQuery("");
    }
  };

  const focusIdle = () => setFocus((f) => (f === "idle" ? null : "idle"));

  // One flag, read in several places, so the live half and the idle half can
  // never both decide they are the visible one.
  const idleOnly = focus === "idle";

  const [, setTick] = useState(0);
  const fetchedAtRef = useRef(Date.now());

  const storeId = userInfo?.store_id;

  // Whether a row's ticket belongs to the store the viewer is signed in to.
  // Loose comparison via String() on purpose: store_id arrives as a number from
  // the login payload and as a string from some query params, and store 0
  // (Kumudu) makes a truthiness check the wrong tool.
  const isOwnStore = (row) =>
    row?.ticket_store_id != null &&
    String(row.ticket_store_id) === String(storeId);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [liveResp, tsResp] = await Promise.all([
        timerService.live(storeId),
        timerService.storeTimesheet(storeId, { date: todayStr() }),
      ]);
      const sessions = liveResp?.data?.active_sessions;
      setRows(Array.isArray(sessions) ? sessions : []);
      fetchedAtRef.current = Date.now();

      const map = {};
      (Array.isArray(tsResp?.data) ? tsResp.data : []).forEach((r) => {
        map[r.user_id] = (map[r.user_id] || 0) + Number(r.total_seconds || 0);
      });
      setTodayByUser(map);
      setLastUpdated(Date.now());
    } catch (e) {
      setRows([]);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId === undefined) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (!cancelled) await fetchAll();
    };
    run();
    const poll = setInterval(run, POLL_MS);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const liveSeconds = (row) =>
    Number(row.running_seconds || 0) +
    Math.floor((Date.now() - fetchedAtRef.current) / 1000);

  // Seconds since the last poll — the board's local tick, applied to the
  // budget the same way it is applied to the clock.
  const sincePoll = () => Math.floor((Date.now() - fetchedAtRef.current) / 1000);

  // Budget left on a row, ticking down between polls the same way the clock
  // ticks up. null when the ticket carries no budget, which is most of them.
  const liveBudgetLeft = (row) => {
    if (row.budget_remaining_seconds == null) return null;
    return Math.max(0, Number(row.budget_remaining_seconds) - sincePoll());
  };

  // How far past its budget a row is, ticking up. A session started after the
  // "budget used up — start anyway?" prompt runs on deliberately, so the board
  // has to show the overrun rather than a countdown frozen at zero: over
  // budget and still going is the state a head most needs to see.
  const liveBudgetOverrun = (row) => {
    if (row.budget_overrun_seconds == null) return null;
    const overrun = Number(row.budget_overrun_seconds);
    return overrun > 0 ? overrun + sincePoll() : 0;
  };

  const isOwnSession = (row) => Number(row?.user_id) === Number(userInfo?.id);

  /**
   * End a session from the board.
   *
   * A head's own row goes through the normal stop, so it is logged as the
   * manual stop it is; anyone else's goes through force-stop, which records
   * who intervened. The distinction is the whole point of having two calls —
   * a timesheet that cannot tell "I finished" from "my head closed this" is
   * missing the fact somebody wants to follow up on.
   */
  const doStop = async (row) => {
    setStoppingId(row.id);
    try {
      const resp = isOwnSession(row)
        ? await timer.stop?.()
        : await timerService.forceStop({
            requested_by: userInfo?.id,
            session_id: row.id,
          });
      setToast({
        severity: resp?.success ? "success" : "error",
        message:
          resp?.statusMessage ||
          (resp?.success ? "Timer stopped" : "Could not stop that timer"),
      });
      if (resp?.success) await fetchAll();
    } catch (e) {
      setToast({ severity: "error", message: "Could not stop that timer" });
    } finally {
      setStoppingId(null);
      setStopTarget(null);
    }
  };

  // Who still works here.
  //
  // users.status is enum('active','inactive','suspended') and the store-users
  // endpoint returns the whole row, so the board already has this — it just
  // never looked. Somebody who has left keeps appearing under Idle for as long
  // as the account exists, which makes the headcount on this screen wrong.
  //
  // Returns null, not an empty Set, until allUsers has actually arrived.
  // Filtering against an empty list would blank the entire board on first
  // paint, which is a worse bug than the one being fixed — so an unknown roster
  // filters nothing.
  const employedUserIds = useMemo(() => {
    if (!Array.isArray(allUsers) || allUsers.length === 0) return null;
    return new Set(
      allUsers
        .filter((u) => String(u?.status ?? "active").toLowerCase() === "active")
        .map((u) => Number(u.id)),
    );
  }, [allUsers]);

  // Applied to the live sessions too, not just the idle list. A leaver should
  // not have a running timer, but if one is left open the board would show a
  // name that is no longer on the team, and the totals would count their time.
  const liveRows = useMemo(
    () =>
      employedUserIds
        ? rows.filter((r) => employedUserIds.has(Number(r.user_id)))
        : rows,
    [rows, employedUserIds],
  );

  const departments = useMemo(() => {
    const m = new Map();
    liveRows.forEach((r) => {
      if (r.dept_id != null) m.set(r.dept_id, r.department_name || "—");
    });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [liveRows]);

  const visibleRows = useMemo(() => {
    let out = liveRows;
    if (dept !== "all") out = out.filter((r) => String(r.dept_id) === String(dept));
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(
        (r) =>
          (r.user_name || "").toLowerCase().includes(q) ||
          (r.ticket_number || "").toLowerCase().includes(q) ||
          (r.description || "").toLowerCase().includes(q) ||
          // The ticket's store is on the card, so it has to be searchable too:
          // a name you can read but cannot type is the more annoying half of a
          // filter.
          (r.ticket_store_name || "").toLowerCase().includes(q),
      );
    }
    out = [...out].sort((a, b) =>
      sort === "name"
        ? (a.user_name || "").localeCompare(b.user_name || "")
        : liveSeconds(b) - liveSeconds(a),
    );
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveRows, dept, query, sort]);

  const activeUserIds = useMemo(
    () => new Set(liveRows.map((r) => Number(r.user_id))),
    [liveRows],
  );
  const idleMembers = useMemo(() => {
    if (!Array.isArray(allUsers)) return [];
    return allUsers
      .filter((u) => String(u?.status ?? "active").toLowerCase() === "active")
      .filter((u) => !activeUserIds.has(Number(u.id)))
      .map((u) => ({ ...u, today: todayByUser[u.id] || 0 }))
      .sort((a, b) => b.today - a.today);
  }, [allUsers, activeUserIds, todayByUser]);

  const totalLiveSeconds = liveRows.reduce((s, r) => s + liveSeconds(r), 0);
  const secsAgo = lastUpdated
    ? Math.max(0, Math.floor((Date.now() - lastUpdated) / 1000))
    : null;

  return (
    <Box
      sx={{
        maxWidth: embedded ? "none" : 980,
        mx: embedded ? 0 : "auto",
        py: embedded ? 0 : 2.5,
        px: embedded ? 0 : { xs: 0.5, sm: 1 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              position: "relative",
              width: 44,
              height: 44,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(62,90,166,0.25)",
            }}
          >
            <SensorsRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
            <Box
              sx={{
                position: "absolute",
                top: -2,
                right: -2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: GREEN,
                border: "2px solid #fff",
                animation: "pulseDot 1.8s infinite",
                "@keyframes pulseDot": {
                  "0%": { boxShadow: `0 0 0 0 ${GREEN}66` },
                  "70%": { boxShadow: `0 0 0 6px ${GREEN}00` },
                  "100%": { boxShadow: `0 0 0 0 ${GREEN}00` },
                },
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 21, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.1 }}>
              Live Activity
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: SUB }}>Who's working on what, right now</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 11, color: MUTED }}>
            {secsAgo === null ? "" : secsAgo < 5 ? "Updated just now" : `Updated ${secsAgo}s ago`}
          </Typography>
          <Tooltip title="Refresh now">
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                color: BRAND,
                bgcolor: "#EEF2FB",
                "&:hover": { bgcolor: "#E4EAF6" },
                animation: refreshing ? "spin 0.8s linear infinite" : "none",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stat tiles */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
        <StatTile icon={<BoltRoundedIcon />} label="Working now" value={liveRows.length} color={GREEN}
          onClick={focusLive} active={focus === "live"} />
        <StatTile icon={<PeopleAltRoundedIcon />} label="Idle / available" value={idleMembers.length} color="#7E8794"
          onClick={focusIdle} active={focus === "idle"} />
        <StatTile icon={<ApartmentRoundedIcon />} label="Departments active" value={departments.length} color={BRAND} />
        <StatTile icon={<AccessTimeRoundedIcon />} label="Total live time" value={formatDuration(totalLiveSeconds)} color="#B98A4E" />
      </Box>

      {/* Controls — live view only. Search and Department filter visibleRows,
          which the idle list does not use, so leaving them on screen would
          offer two controls that quietly do nothing. */}
      <Box sx={{ display: idleOnly ? "none" : "flex", gap: 1, mb: 2.5, flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
        <TextField
          size="small"
          placeholder="Search person, ticket, store, description"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField size="small" select label="Department" value={dept} onChange={(e) => setDept(e.target.value)}
          sx={{ minWidth: 165, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
          <MenuItem value="all">All departments</MenuItem>
          {departments.map((d) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
        </TextField>
        <TextField size="small" select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}
          sx={{ minWidth: 155, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
          <MenuItem value="duration">Longest running</MenuItem>
          <MenuItem value="name">Name (A–Z)</MenuItem>
        </TextField>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: BRAND }} />
        </Box>
      )}

      {!loading && !idleOnly && liveRows.length === 0 && (
        <Box sx={{ ...CARD, textAlign: "center", py: 7, px: 2, color: MUTED }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", mx: "auto", mb: 1.5, bgcolor: "#F2F4F8", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SensorsRoundedIcon sx={{ fontSize: 28, color: "#D3D8E0" }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#4A515F" }}>All quiet right now</Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>Nobody has a timer running.</Typography>
        </Box>
      )}

      {!loading && !idleOnly && liveRows.length > 0 && visibleRows.length === 0 && (
        <Typography sx={{ color: MUTED, textAlign: "center", py: 4 }}>No one matches this filter.</Typography>
      )}

      {/* Active session cards */}
      {!loading && !idleOnly && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {visibleRows.map((r) => {
            const live = liveSeconds(r);
            const long = live >= LONG_RUNNING_SECONDS;
            const accent = PRIO[r.priority] || "#9AA2B1";
            const todaySoFar = (todayByUser[r.user_id] || 0) + live;
            const budgetLeft = liveBudgetLeft(r);
            const budgetOver = liveBudgetOverrun(r);
            const overBudget = budgetOver > 0;
            const budgetTight =
              budgetLeft !== null && budgetLeft <= BUDGET_WARN_SECONDS;
            // With a budget, the bar measures the thing that will actually end
            // this session; without one it falls back to the 2h "running long"
            // mark, which is the only yardstick such a ticket has.
            const progress =
              budgetLeft === null
                ? Math.min(1, live / LONG_RUNNING_SECONDS)
                : overBudget
                  ? 1
                  : live / Math.max(1, live + budgetLeft);
            return (
              <Box
                key={r.id}
                sx={{
                  ...CARD,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  borderLeft: `4px solid ${long ? AMBER : accent}`,
                  px: 1.75,
                  py: 1.4,
                  bgcolor: long ? AMBER_BG : "#fff",
                  transition: "box-shadow 0.18s ease, transform 0.18s ease",
                  "&:hover": { boxShadow: "0 5px 16px rgba(16,24,40,0.08)", transform: "translateY(-1px)" },
                }}
              >
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Avatar sx={{ bgcolor: avatarColor(r.user_name), width: 44, height: 44, fontSize: 16, fontWeight: 700 }}>
                    {initials(r.user_name)}
                  </Avatar>
                  <Box sx={{ position: "absolute", bottom: -1, right: -1, width: 13, height: 13, borderRadius: "50%", bgcolor: long ? AMBER : GREEN, border: "2px solid #fff" }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{r.user_name}</Typography>
                    <Chip label={`#${r.ticket_number}`} size="small"
                      sx={{ height: 19, fontSize: 10.5, fontWeight: 600, bgcolor: "#EEF2FB", color: BRAND, "& .MuiChip-label": { px: 0.9 } }} />
                    {r.department_name && (
                      <Chip label={r.department_name} size="small"
                        sx={{ height: 19, fontSize: 10.5, bgcolor: "#F1F3F7", color: "#5A6474", "& .MuiChip-label": { px: 0.9 } }} />
                    )}
                    {/* Which store's ticket this is.

                        The board is scoped by the WORKER's store, not the
                        ticket's, so that somebody working another store's ticket
                        still appears here — which meant a row could be anyone's
                        ticket and there was no way to tell. Kumudu staff
                        routinely have three stores' tickets running side by side.

                        Another store's ticket is tinted, the viewer's own is
                        quiet: the whole reason to read this is to spot the ones
                        that are not yours, and most rows are. */}
                    {r.ticket_store_name && (
                      <Chip
                        icon={<StorefrontRoundedIcon sx={{ fontSize: 11 }} />}
                        label={r.ticket_store_name}
                        size="small"
                        title={`Ticket belongs to ${r.ticket_store_name}`}
                        sx={{
                          height: 19,
                          fontSize: 10.5,
                          fontWeight: isOwnStore(r) ? 400 : 700,
                          bgcolor: isOwnStore(r) ? "#F1F3F7" : "#EDF1FB",
                          color: isOwnStore(r) ? "#5A6474" : BRAND,
                          "& .MuiChip-label": { px: 0.9 },
                          "& .MuiChip-icon": {
                            color: isOwnStore(r) ? "#9AA2B1" : BRAND,
                            ml: 0.6,
                            mr: -0.3,
                          },
                        }}
                      />
                    )}
                    {r.priority && (
                      <Chip label={r.priority} size="small"
                        sx={{ height: 19, fontSize: 10.5, textTransform: "capitalize", bgcolor: `${accent}1F`, color: accent, fontWeight: 700, "& .MuiChip-label": { px: 0.9 } }} />
                    )}
                    {long && (
                      <Chip icon={<WarningAmberRoundedIcon sx={{ fontSize: 12 }} />} label="Running long" size="small"
                        sx={{ height: 19, fontSize: 10, bgcolor: "#F7EBD6", color: AMBER, fontWeight: 700, "& .MuiChip-icon": { color: AMBER, ml: 0.5 } }} />
                    )}
                    {/* The budget, as time left rather than time allowed.
                        It is a countdown to an automatic stop, so what a head
                        needs to see is who is about to hit it. */}
                    {budgetLeft !== null && (
                      <Chip
                        icon={<HourglassBottomRoundedIcon sx={{ fontSize: 12 }} />}
                        label={
                          overBudget
                            ? `over budget by ${formatDuration(budgetOver)}`
                            : `${formatDuration(budgetLeft)} of ${r.time_budget_minutes}m left`
                        }
                        size="small"
                        title={
                          overBudget
                            ? `Past its ${r.time_budget_minutes} min budget — started with the overrun accepted`
                            : `Time budget ${r.time_budget_minutes} min — the timer stops by itself when it runs out`
                        }
                        sx={{
                          height: 19,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: overBudget
                            ? "#FBECEC"
                            : budgetTight
                              ? "#F7EBD6"
                              : "#F1F3F7",
                          color: overBudget
                            ? "#B4413F"
                            : budgetTight
                              ? AMBER
                              : "#5A6474",
                          "& .MuiChip-label": { px: 0.9 },
                          "& .MuiChip-icon": {
                            color: overBudget
                              ? "#B4413F"
                              : budgetTight
                                ? AMBER
                                : "#9AA2B1",
                            ml: 0.6,
                            mr: -0.3,
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ mt: 0.35 }}>
                    <ExpandableText text={r.description} sx={{ fontSize: 12.5, color: "#3A4150" }} />
                  </Box>
                  <Typography sx={{ fontSize: 10.5, color: MUTED, mt: 0.35 }}>
                    Since {fmtSince(r.started_at)} · Today {formatDuration(todaySoFar)}
                  </Typography>
                </Box>

                {/* Live clock pill */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, flexShrink: 0, px: 1.5, py: 0.9, borderRadius: 2.5, bgcolor: long ? AMBER_BG : GREEN_BG, minWidth: 92 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: long ? AMBER : GREEN }}>
                    <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                    <Typography sx={{ fontFamily: "monospace", fontSize: 17, fontWeight: 800, letterSpacing: "0.5px" }}>
                      {formatClock(live)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: "100%", height: 3, borderRadius: 2, bgcolor: long ? "#EAD6AE" : "#CFE9D8", overflow: "hidden" }}>
                    <Box sx={{ width: `${progress * 100}%`, height: "100%", bgcolor: long ? AMBER : GREEN, transition: "width 0.5s ease" }} />
                  </Box>

                  {/* A Head's stop. Deliberately a small text button rather
                      than an icon: it ends somebody else's session, and that
                      is worth a word instead of a glyph you could hit by
                      accident on a phone. */}
                  {canStopOthers && (
                    <Button
                      size="small"
                      onClick={() => setStopTarget(r)}
                      disabled={stoppingId === r.id}
                      startIcon={
                        stoppingId === r.id ? (
                          <CircularProgress size={11} thickness={5} color="inherit" />
                        ) : (
                          <StopCircleRoundedIcon sx={{ fontSize: 14 }} />
                        )
                      }
                      sx={{
                        mt: 0.3,
                        minWidth: 0,
                        px: 0.75,
                        py: 0.1,
                        fontSize: 10.5,
                        fontWeight: 700,
                        textTransform: "none",
                        color: "#B4413F",
                        "&:hover": { bgcolor: "#FBECEC" },
                        "& .MuiButton-startIcon": { mr: 0.4 },
                      }}
                    >
                      Stop
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Ending somebody else's session asks first, and says what happens to
          the time: the fear that stopping a colleague's clock deletes their
          afternoon is exactly why a head would otherwise leave a runaway
          timer alone. */}
      <Dialog
        open={!!stopTarget}
        onClose={() => setStopTarget(null)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380 } }}
      >
        <DialogTitle sx={{ fontSize: 16.5, fontWeight: 800, color: INK, pb: 0.5 }}>
          {stopTarget && isOwnSession(stopTarget)
            ? "Stop your timer?"
            : `Stop ${stopTarget?.user_name || "this"} timer?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5, color: SUB }}>
            {stopTarget && (
              <>
                #{stopTarget.ticket_number} has been running for{" "}
                <b>{formatDuration(liveSeconds(stopTarget))}</b>. That time is
                kept on the timesheet — stopping only ends the session.
                {!isOwnSession(stopTarget) &&
                  " It will be logged as stopped by you."}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setStopTarget(null)}
            sx={{ textTransform: "none", color: SUB, fontWeight: 600 }}
          >
            Keep running
          </Button>
          <Button
            onClick={() => doStop(stopTarget)}
            disabled={!!stoppingId}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: "#B4413F",
              "&:hover": { bgcolor: "#9C3634" },
            }}
          >
            Stop timer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity || "info"}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ fontSize: 13, borderRadius: 2 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>

      {/* Idle / available — only as the board, never as a strip underneath.
          The collapsible version that used to sit below the live list is gone:
          the tile is the way in, so a second control for the same thing was
          just a row of chrome on every page load. */}
      {!loading && idleOnly && idleMembers.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#3A4150" }}>Idle / available</Typography>
            <Box sx={{ px: 1, py: 0.1, borderRadius: 999, bgcolor: "#F1F3F7" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: SUB }}>{idleMembers.length}</Typography>
            </Box>
            <Typography
              onClick={() => setFocus(null)}
              sx={{ ml: "auto", fontSize: 12.5, fontWeight: 700, color: BRAND, cursor: "pointer" }}
            >
              Show everyone
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {idleMembers.map((u) => (
                <Box key={u.id} sx={{ ...CARD, display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.85, bgcolor: "#FCFCFE", minWidth: 190 }}>
                  <Avatar sx={{ bgcolor: "#E7EAF0", color: "#6B7280", width: 30, height: 30, fontSize: 12.5, fontWeight: 700 }}>
                    {initials(u.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#3A4150" }} noWrap>{u.name}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: MUTED }}>
                      {u.today > 0 ? `Today ${formatDuration(u.today)}` : "No time today"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LiveActivityDashboard;
